import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tooltip, CircularProgress, Grid,
  MenuItem, Chip, Menu, ListItemIcon, ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedIcon from '@mui/icons-material/Verified';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import { useFirebase } from '../../context/FirebaseContext';
import { useJournalEnabled, useTradeHistoryWebhookEnabled } from '../../context/FeatureFlagContext';
import { useNavigate } from 'react-router-dom';
import './TraderJournalView.css';
import logger from '../../utils/logger';
import { Logo } from '../layout';
import { analytics } from '../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import {
  getJournalEntriesV2,
  getJournalEntryV2,
  updateJournalEntryV2,
  deleteJournalEntryV2,
  getJournalStatisticsV2,
  getJournalAccountsV2,
  createJournalAccountV2,
  updateJournalAccountV2,
  deleteJournalAccountV2
} from '../../api/journal';
import JournalEntryForm from './JournalEntryForm';
import AccountForm from './AccountForm';
import DeleteAccountDialog from './DeleteAccountDialog';
import PnLCalendar from './PnLCalendar';
import EquityCurve from './EquityCurve';
import JournalFilterBar from './JournalFilterBar';
import JournalStats from './JournalStats';
import AccountSettingsModal from './AccountSettingsModal';

// Main component
const TraderJournalView = ({ onClose, isEmbedded }) => {
  return <TraderJournalContent onClose={onClose} isEmbedded={isEmbedded} />;
};

// Content component
const TraderJournalContent = ({ onClose, isEmbedded }) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table'); // 'table', 'calendar', 'analytics'
  const [entries, setEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]); // Unfiltered entries for calendar/charts
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: 'today',  // Default to today
    outcome: 'all',
    instrument: '',
    account: '',
    side: '',
    validation: '',
    source: ''
  });
  const { auth } = useFirebase();
  const journalEnabled = useJournalEnabled();
  const webhookFeatureEnabled = useTradeHistoryWebhookEnabled();
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentEntry, setCurrentEntry] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountValidationErrors, setAccountValidationErrors] = useState({});
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [accountForSettings, setAccountForSettings] = useState(null);

  // Get unique instruments from entries
  const instruments = useMemo(() => {
    const unique = [...new Set(allEntries.map(e => e.instrument).filter(Boolean))];
    return unique.sort();
  }, [allEntries]);

  // Get unique source IDs from entries
  const sourceIds = useMemo(() => {
    const unique = [...new Set(allEntries.map(e => e.source_id).filter(Boolean))];
    return unique.sort();
  }, [allEntries]);

  // Load all entries
  const loadEntries = useCallback(async () => {
    if (!auth?.currentUser) return;

    try {
      setLoading(true);
      // Load all entries without account filter, methodAnalytics='all'
      const response = await new Promise((resolve) => {
        getJournalEntriesV2(auth, resolve, 1000, 0, 'all', null);
      });

      if (Array.isArray(response) && response.length > 0) {
        setAllEntries(response);
      } else if (response.data) {
        setAllEntries(response.data);
      } else {
        setAllEntries([]);
      }
    } catch (error) {
      logger.error('Error loading journal entries:', error);
      setAllEntries([]);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  // Filter entries based on current filters
  const filteredEntries = useMemo(() => {
    let result = [...allEntries];

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      let cutoff = new Date();

      switch (filters.dateRange) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case '7d':
          cutoff.setDate(now.getDate() - 7);
          break;
        case '30d':
          cutoff.setDate(now.getDate() - 30);
          break;
        case '90d':
          cutoff.setDate(now.getDate() - 90);
          break;
        case 'ytd':
          cutoff = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          cutoff = new Date(0);
      }

      result = result.filter(e => new Date(e.trade_date) >= cutoff);
    }

    // Outcome filter
    if (filters.outcome === 'win') {
      result = result.filter(e => e.profit_loss > 0);
    } else if (filters.outcome === 'loss') {
      result = result.filter(e => e.profit_loss < 0);
    }

    // Instrument filter
    if (filters.instrument) {
      result = result.filter(e => e.instrument === filters.instrument);
    }

    // Account filter
    if (filters.account) {
      result = result.filter(e => e.account_id === parseInt(filters.account));
    }

    // Side filter
    if (filters.side) {
      result = result.filter(e => e.trade_type === filters.side);
    }

    // Validation filter
    if (filters.validation) {
      result = result.filter(e => {
        const validationData = parseValidationData(e.lessons);
        if (filters.validation === 'none') {
          return !isConceptGateTrade(e) || !validationData?.validationStatus;
        }
        return validationData?.validationStatus === filters.validation;
      });
    }

    // Source filter
    if (filters.source) {
      result = result.filter(e => e.source_id === filters.source);
    }

    return result;
  }, [allEntries, filters]);

  // Update entries for display
  useEffect(() => {
    setEntries(filteredEntries);
  }, [filteredEntries]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    if (!auth?.currentUser) return;

    try {
      await getJournalStatisticsV2(
        auth,
        (data) => {
          if (!data.error) {
            setStatistics(data);
          }
        },
        'all',
        null
      );
    } catch (error) {
      logger.error('Failed to load statistics:', error);
    }
  }, [auth]);

  // Load accounts
  const loadAccounts = useCallback(async () => {
    if (!auth?.currentUser) return;

    try {
      await getJournalAccountsV2(auth, (data) => {
        if (!data.error) {
          setAccounts(data);
        }
      });
    } catch (error) {
      logger.error('Failed to load accounts:', error);
    }
  }, [auth]);

  // Initial data load
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadEntries();
    loadStatistics();
  }, [loadEntries, loadStatistics]);

  // Parse validation data from lessons JSON
  const parseValidationData = (lessonsStr) => {
    if (!lessonsStr) return null;
    try {
      const data = JSON.parse(lessonsStr);
      if (data.validationStatus || data.gates) return data;
      if (Array.isArray(data)) return { gates: data };
      return null;
    } catch {
      return null;
    }
  };

  // Check if entry is a concept_gate trade
  const isConceptGateTrade = (entry) => {
    return entry?.method_analytics === 'concept_gate';
  };

  const handleViewEntry = async (entryId) => {
    try {
      setLoading(true);
      const response = await new Promise((resolve) => {
        getJournalEntryV2(auth, resolve, entryId);
      });

      if (response && !response.error) {
        setSelectedEntry(response);
        setViewDialogOpen(true);
        logEvent(analytics, 'journal_entry_viewed', {
          instrument: response.instrument,
          trade_type: response.trade_type
        });
      }
    } catch (error) {
      logger.error('Error loading entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setCurrentEntry({
      title: entry.title || '',
      content: entry.content || '',
      instrument: entry.instrument || '',
      trade_type: entry.trade_type || '',
      profit_loss: entry.profit_loss || 0,
      return_percentage: entry.return_percentage || 0,
      entry_price: entry.entry_price || 0,
      exit_price: entry.exit_price || 0,
      entry_size: entry.entry_size || 0,
      trade_date: entry.trade_date || Date.now(),
      mood: entry.mood || '',
      lessons: entry.lessons || '',
      method_analytics: entry.method_analytics || 'Manual'
    });
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleSaveEntry = async () => {
    try {
      setLoading(true);
      await new Promise((resolve) => {
        updateJournalEntryV2(auth, resolve, selectedEntry.entry_id, currentEntry);
      });
      await loadEntries();
      await loadStatistics();
      setEditDialogOpen(false);
      setValidationErrors({});
    } catch (error) {
      logger.error('Error saving entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!selectedEntry?.entry_id) return;

    try {
      setLoading(true);
      await new Promise((resolve) => {
        deleteJournalEntryV2(auth, resolve, selectedEntry.entry_id);
      });
      await loadEntries();
      await loadStatistics();
      setSelectedEntry(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      logger.error('Error deleting entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEntryChange = (field, value) => {
    if (field === 'trade_date') {
      const dateObj = new Date(value);
      if (!isNaN(dateObj.getTime())) {
        setCurrentEntry(prev => ({ ...prev, trade_date: dateObj.getTime() }));
      }
      return;
    }

    if (['entry_size', 'profit_loss', 'return_percentage', 'entry_price', 'exit_price'].includes(field)) {
      const numericValue = value === '' ? null : parseFloat(value);
      setCurrentEntry(prev => ({ ...prev, [field]: numericValue }));
    } else {
      setCurrentEntry(prev => ({ ...prev, [field]: value }));
    }

    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleExport = () => {
    const headers = ['Date', 'Instrument', 'Source', 'Side', 'Entry', 'Exit', 'P/L', 'Return %'];
    const rows = entries.map(e => [
      formatDate(e.trade_date),
      e.instrument,
      e.source_id || '',
      e.trade_type,
      e.entry_price,
      e.exit_price,
      e.profit_loss,
      e.return_percentage
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: 'today',  // Reset to today
      outcome: 'all',
      instrument: '',
      account: '',
      side: '',
      validation: '',
      source: ''
    });
  };

  const handleCreateAccount = async (formData) => {
    try {
      await createJournalAccountV2(auth, (data) => {
        if (!data.error) {
          setAccounts(prev => [...prev, data]);
          setShowAccountForm(false);
        }
      }, formData);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleEditAccount = async (formData) => {
    try {
      await updateJournalAccountV2(auth, (data) => {
        if (!data.error) {
          setAccounts(prev => prev.map(acc =>
            acc.account_id === selectedAccount.account_id ? { ...acc, ...formData } : acc
          ));
          setShowAccountForm(false);
          setSelectedAccount(null);
        }
      }, selectedAccount.account_id, formData);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    const account = accounts.find(acc => acc.account_id === accountId);
    if (account) {
      setAccountToDelete(account);
      setDeleteAccountDialog(true);
    }
  };

  const handleAccountSettingsUpdate = (updatedAccount) => {
    setAccounts(prev => prev.map(acc =>
      acc.account_id === updatedAccount.account_id ? { ...acc, ...updatedAccount } : acc
    ));
  };

  const confirmDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      await deleteJournalAccountV2(auth, (data) => {
        if (!data.error) {
          setAccounts(prev => prev.filter(acc => acc.account_id !== accountToDelete.account_id));
          if (selectedAccount?.account_id === accountToDelete.account_id) {
            setSelectedAccount(null);
          }
        }
      }, accountToDelete.account_id);
    } catch (error) {
      setError(error.message);
    } finally {
      setDeleteAccountDialog(false);
      setAccountToDelete(null);
    }
  };

  const handleDayClick = (dateKey, dayData) => {
    setFilters(prev => ({ ...prev, dateRange: 'custom' }));
    // Could implement custom date filtering here
    logger.log('Day clicked:', dateKey, dayData);
  };

  return (
    <Box className="journal-container" sx={{
      height: isEmbedded ? '100%' : 'auto',
      minHeight: isEmbedded ? 0 : undefined,
      overflow: isEmbedded ? 'auto' : 'visible',
      display: isEmbedded ? 'flex' : 'block',
      flexDirection: 'column',
      WebkitOverflowScrolling: 'touch'
    }}>
      {/* Header */}
      <Box className="journal-header" sx={{ padding: isEmbedded ? '8px 16px' : undefined }}>
        <Box className="header-left">
          {isEmbedded ? (
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={onClose}
              size="small"
              className="back-btn"
            >
              Close
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              size="small"
              className="back-btn"
            >
              Back
            </Button>
          )}

          {!isEmbedded && (
            <>
              <Box className="logo-wrap" onClick={() => navigate('/')}>
                <Logo size="32px" />
              </Box>
              <Box className="breadcrumb">
                <Typography component="span" onClick={() => navigate('/')}>
                  NanoTradingCopilot
                </Typography>
                <Typography component="span" className="separator">/</Typography>
                <Typography component="span" className="current">
                  Trading Journal
                </Typography>
              </Box>
            </>
          )}

          {isEmbedded && (
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 500 }}>
              Trading Journal
            </Typography>
          )}
        </Box>

        {/* Account management button */}
        <Box className="header-right">
          <Tooltip title="Manage Accounts">
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
              className="accounts-btn"
            >
              Accounts
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Panel */}
      <JournalStats entries={filteredEntries} statistics={statistics} />

      {/* Filter Bar */}
      <JournalFilterBar
        filters={filters}
        onFilterChange={setFilters}
        instruments={instruments}
        sourceIds={sourceIds}
        accounts={accounts}
        onExport={handleExport}
        onRefresh={() => { loadEntries(); loadStatistics(); }}
        onClearFilters={handleClearFilters}
        totalEntries={allEntries.length}
        filteredCount={filteredEntries.length}
        loading={loading}
      />

      {/* View Mode Tabs */}
      <Box className="view-tabs-container">
        <Tabs
          value={viewMode}
          onChange={(e, val) => setViewMode(val)}
          className="view-tabs"
        >
          <Tab
            value="table"
            icon={<TableChartIcon />}
            label="Trades"
            iconPosition="start"
          />
          {journalEnabled && (
            <Tab
              value="calendar"
              icon={<CalendarMonthIcon />}
              label="Calendar"
              iconPosition="start"
            />
          )}
          {journalEnabled && (
            <Tab
              value="analytics"
              icon={<BarChartIcon />}
              label="Analytics"
              iconPosition="start"
            />
          )}
        </Tabs>
      </Box>

      {/* Main Content */}
      <Box className="journal-content" sx={{ flex: isEmbedded ? 1 : undefined, minHeight: isEmbedded ? 0 : undefined }}>
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress />
          </Box>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <TableContainer component={Paper} className="trades-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell>Entry / Exit</TableCell>
                  <TableCell>Validation</TableCell>
                  <TableCell>P/L</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" className="empty-row">
                      <Typography variant="body2" color="textSecondary">
                        No trades found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => {
                    const validationData = isConceptGateTrade(entry) ? parseValidationData(entry.lessons) : null;

                    return (
                      <TableRow key={entry.entry_id} hover>
                        <TableCell>
                          <Chip
                            label={entry.profit_loss >= 0 ? "WIN" : "LOSS"}
                            size="small"
                            className={`status-chip ${entry.profit_loss >= 0 ? 'win' : 'loss'}`}
                          />
                        </TableCell>
                        <TableCell className="date-cell">
                          {formatDate(entry.trade_date)}
                        </TableCell>
                        <TableCell>
                          <Typography className="instrument-name">
                            {entry.instrument}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {entry.source_id ? (
                            <Chip
                              label={entry.source_id}
                              size="small"
                              className="source-chip"
                              variant="outlined"
                            />
                          ) : (
                            <span className="no-source">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.trade_type}
                            size="small"
                            className={`side-chip ${entry.trade_type?.toLowerCase()}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Box className="price-cell">
                            <span className="entry-price">
                              {entry.entry_price ? `$${entry.entry_price.toFixed(2)}` : '-'}
                            </span>
                            <span className="exit-price">
                              → {entry.exit_price ? `$${entry.exit_price.toFixed(2)}` : '-'}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {isConceptGateTrade(entry) && validationData?.validationStatus ? (
                            <Chip
                              icon={validationData.validationStatus === 'GO' ?
                                <VerifiedIcon style={{ fontSize: 14 }} /> :
                                <CancelIcon style={{ fontSize: 14 }} />}
                              label={validationData.validationStatus === 'GO' ? 'PASSED' : 'MISMATCHED'}
                              size="small"
                              className={`validation-chip ${validationData.validationStatus.toLowerCase().replace('_', '-')}`}
                            />
                          ) : (
                            <span className="no-validation">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box className="pl-cell">
                            <Box className="pl-amount">
                              {entry.profit_loss >= 0 ? (
                                <TrendingUpIcon className="pl-icon positive" />
                              ) : (
                                <TrendingDownIcon className="pl-icon negative" />
                              )}
                              <span className={entry.profit_loss >= 0 ? 'positive' : 'negative'}>
                                ${Math.abs(entry.profit_loss).toFixed(2)}
                              </span>
                            </Box>
                            {entry.return_percentage != null && (
                              <span className={`pl-percent ${entry.return_percentage >= 0 ? 'positive' : 'negative'}`}>
                                {entry.return_percentage >= 0 ? '+' : ''}{entry.return_percentage.toFixed(1)}%
                              </span>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="actions-cell">
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => handleViewEntry(entry.entry_id)}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isConceptGateTrade(entry) && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    onClick={() => handleEditEntry(entry)}
                                    size="small"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    onClick={() => {
                                      setSelectedEntry(entry);
                                      setDeleteDialogOpen(true);
                                    }}
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Calendar View - Feature flag required */}
        {viewMode === 'calendar' && journalEnabled && (
          <PnLCalendar
            entries={filteredEntries}
            onDayClick={handleDayClick}
          />
        )}

        {/* Analytics View - Feature flag required */}
        {viewMode === 'analytics' && journalEnabled && (
          <Box className="analytics-view">
            <EquityCurve entries={filteredEntries} height={350} />
          </Box>
        )}
      </Box>

      {/* View Entry Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'journal-dialog' }}
      >
        <DialogTitle className="dialog-header">
          <Typography variant="h6">
            {selectedEntry?.instrument} {selectedEntry?.trade_type}
          </Typography>
          <IconButton onClick={() => setViewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box className="detail-section">
                <Typography className="detail-label">Date</Typography>
                <Typography className="detail-value">{formatDate(selectedEntry?.trade_date)}</Typography>
              </Box>
              <Box className="detail-section">
                <Typography className="detail-label">Side</Typography>
                <Chip
                  label={selectedEntry?.trade_type}
                  size="small"
                  className={`side-chip ${selectedEntry?.trade_type?.toLowerCase()}`}
                />
              </Box>
              <Box className="detail-section">
                <Typography className="detail-label">Entry / Exit Price</Typography>
                <Typography className="detail-value">
                  ${selectedEntry?.entry_price?.toFixed(2) || '-'} → ${selectedEntry?.exit_price?.toFixed(2) || '-'}
                </Typography>
              </Box>
              {selectedEntry?.source_id && (
                <Box className="detail-section">
                  <Typography className="detail-label">Source</Typography>
                  <Chip
                    label={selectedEntry.source_id}
                    size="small"
                    className="source-chip"
                    variant="outlined"
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="detail-section">
                <Typography className="detail-label">P/L</Typography>
                <Typography className={`detail-value large ${selectedEntry?.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                  {selectedEntry?.profit_loss >= 0 ? '+' : ''}${selectedEntry?.profit_loss?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
              <Box className="detail-section">
                <Typography className="detail-label">Return %</Typography>
                <Typography className={`detail-value ${selectedEntry?.return_percentage >= 0 ? 'positive' : 'negative'}`}>
                  {selectedEntry?.return_percentage >= 0 ? '+' : ''}{selectedEntry?.return_percentage?.toFixed(2) || '0'}%
                </Typography>
              </Box>
            </Grid>

            {/* Validation Gates Section */}
            {isConceptGateTrade(selectedEntry) && (() => {
              const validationData = parseValidationData(selectedEntry?.lessons);
              if (!validationData) return null;

              return (
                <Grid item xs={12}>
                  <Box className="validation-section">
                    <Box className="validation-header">
                      <Typography className="section-title">Validation Status</Typography>
                      {validationData.validationStatus && (
                        <Chip
                          icon={validationData.validationStatus === 'GO' ?
                            <VerifiedIcon style={{ fontSize: 14 }} /> :
                            <CancelIcon style={{ fontSize: 14 }} />}
                          label={validationData.validationStatus === 'GO' ? 'PASSED' : 'MISMATCHED'}
                          size="small"
                          className={`validation-chip ${validationData.validationStatus.toLowerCase().replace('_', '-')}`}
                        />
                      )}
                      {validationData.gateStats && (
                        <Typography className="gate-stats">
                          {validationData.gateStats.passed} passed, {validationData.gateStats.failed} failed
                        </Typography>
                      )}
                    </Box>

                    {validationData.tradeSetup && (
                      <Box className="trade-setup">
                        <Grid container spacing={2}>
                          {validationData.tradeSetup.stopPrice && (
                            <Grid item xs={3}>
                              <Typography className="setup-label">Stop</Typography>
                              <Typography className="setup-value negative">
                                ${validationData.tradeSetup.stopPrice}
                              </Typography>
                            </Grid>
                          )}
                          {validationData.tradeSetup.targetPrice && (
                            <Grid item xs={3}>
                              <Typography className="setup-label">Target</Typography>
                              <Typography className="setup-value positive">
                                ${validationData.tradeSetup.targetPrice}
                              </Typography>
                            </Grid>
                          )}
                          {validationData.tradeSetup.riskAmount && (
                            <Grid item xs={3}>
                              <Typography className="setup-label">Risk</Typography>
                              <Typography className="setup-value">
                                ${validationData.tradeSetup.riskAmount}
                              </Typography>
                            </Grid>
                          )}
                          {validationData.tradeSetup.desiredRr && (
                            <Grid item xs={3}>
                              <Typography className="setup-label">R:R</Typography>
                              <Typography className="setup-value blue">
                                1:{validationData.tradeSetup.desiredRr.toFixed(1)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {validationData.gates && validationData.gates.length > 0 && (
                      <Box className="gates-list">
                        {validationData.gates.map((gate, idx) => (
                          <Box key={idx} className={`gate-item ${gate.status?.toLowerCase()}`}>
                            {gate.status === 'PASSED' ? (
                              <CheckCircleIcon className="gate-icon" />
                            ) : (
                              <CancelIcon className="gate-icon" />
                            )}
                            <Box className="gate-info">
                              <Typography className="gate-name">{gate.name}</Typography>
                              {gate.message && (
                                <Typography className="gate-message">{gate.message}</Typography>
                              )}
                            </Box>
                            {gate.type && (
                              <Chip label={gate.type} size="small" className="gate-type" />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })()}

            {selectedEntry?.content && (
              <Grid item xs={12}>
                <Box className="detail-section">
                  <Typography className="detail-label">Notes</Typography>
                  <Paper className="notes-content">
                    <Typography>{selectedEntry.content}</Typography>
                  </Paper>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions className="dialog-footer">
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {!isConceptGateTrade(selectedEntry) && (
            <Button
              variant="contained"
              onClick={() => {
                setViewDialogOpen(false);
                handleEditEntry(selectedEntry);
              }}
            >
              Edit Entry
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Entry Dialog */}
      <JournalEntryForm
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setValidationErrors({});
        }}
        onSubmit={handleSaveEntry}
        entry={currentEntry}
        isEditing={true}
        loading={loading}
        validationErrors={validationErrors}
        onFieldChange={handleEntryChange}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ className: 'journal-dialog' }}
      >
        <DialogTitle className="dialog-header">
          <Typography variant="h6">Confirm Delete</Typography>
          <IconButton onClick={() => setDeleteDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialog-content">
          <Typography>
            Are you sure you want to delete this journal entry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className="dialog-footer">
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteEntry}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Account Management */}
      <AccountForm
        open={showAccountForm}
        onClose={() => {
          setShowAccountForm(false);
          setSelectedAccount(null);
          setAccountValidationErrors({});
        }}
        onSubmit={selectedAccount ? handleEditAccount : handleCreateAccount}
        account={selectedAccount}
        loading={loading}
        validationErrors={accountValidationErrors}
        onFieldChange={() => { }}
      />

      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={() => setAccountMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          setShowAccountForm(true);
          setAccountMenuAnchor(null);
        }}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          Create New Account
        </MenuItem>
        {accounts.map((account) => (
          <MenuItem key={account.account_id}>
            <ListItemText primary={account.name} />
            {webhookFeatureEnabled && (
              <Tooltip title="Settings">
                <IconButton size="small" onClick={() => {
                  setAccountForSettings(account);
                  setAccountSettingsOpen(true);
                  setAccountMenuAnchor(null);
                }}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => {
              setSelectedAccount(account);
              setShowAccountForm(true);
            }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteAccount(account.account_id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      <DeleteAccountDialog
        open={deleteAccountDialog}
        onClose={() => {
          setDeleteAccountDialog(false);
          setAccountToDelete(null);
        }}
        onConfirm={confirmDeleteAccount}
        account={accountToDelete}
        loading={loading}
      />

      {/* Account Settings Modal for Webhook */}
      <AccountSettingsModal
        account={accountForSettings}
        open={accountSettingsOpen}
        onClose={() => {
          setAccountSettingsOpen(false);
          setAccountForSettings(null);
        }}
        onAccountUpdate={handleAccountSettingsUpdate}
      />
    </Box>
  );
};

export default TraderJournalView;
